import { useEffect, useMemo, useState } from 'react';

import { useMutation, useQuery } from '@apollo/client/react';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  FormLabel,
  Skeleton,
  Snackbar,
  Stack,
  Tooltip,
} from '@mui/material';
import { CheckIcon, RefreshCwIcon, SaveIcon } from 'lucide-react';
import { useForm } from 'react-hook-form';

import { gql } from '@graphql/__generated__';
import { RaTextField } from '@modules/_shared/components/form/RaTextfield';
import { extractMessageFromError } from '@modules/_shared/utils/parseError';
import { useLocale } from '@shared/hooks/locale';

const GET_LISTING_REFERENCES = gql(/* GraphQL */ `
  query GetListingReferences($id: uuid!) {
    get_listing_references(id: $id) {
      id
      reference_aliases_encoded {
        alias
        created_at
      }
      listing {
        id
        lot_reference
      }
    }
  }
`);

const REFRESH_REFERENCE = gql(/* GraphQL */ `
  mutation RefreshReference($id: uuid!) {
    insert_lot_reference_aliases_one(object: { lot_id: $id }) {
      reference_alias_decoded
    }
  }
`);

type ListingReferencesProps = {
  listingId: string;
  readOnly?: boolean;
  onReferenceChanged: (value: string | null) => Promise<any>;
};

const ListingReferencesSkeleton: React.FC = () => {
  return (
    <Stack direction="row" spacing={2}>
      <Skeleton
        width={80}
        variant="rounded"
        sx={{ borderRadius: 16, height: 30 }}
      />
      <Skeleton
        width={80}
        variant="rounded"
        sx={{ borderRadius: 16, height: 30 }}
      />
      <Skeleton
        width={80}
        variant="rounded"
        sx={{ borderRadius: 16, height: 30 }}
      />
    </Stack>
  );
};

const ReferenceForm: React.FC<{
  listingRef: string | null;
  readOnly?: boolean;
  onSubmit: (data: { lot_reference: string | null }) => Promise<any>;
}> = ({ listingRef, onSubmit, readOnly = false }) => {
  const { t } = useLocale();
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isDirty, isSubmitting },
    reset,
  } = useForm<{ lot_reference: string | null }>({
    defaultValues: { lot_reference: listingRef },
  });

  useEffect(() => {
    if (listingRef) {
      reset({ lot_reference: listingRef });
    }
  }, [listingRef, reset]);

  const [snackErrorOpened, setSnackErrorOpened] = useState(false);

  const handleSnackClose = (
    _event: React.SyntheticEvent | Event,
    reason?: string,
  ) => {
    if (reason === 'clickaway') {
      return;
    }

    setSnackErrorOpened(false);
  };

  const submit = async (formData: { lot_reference: string | null }) => {
    try {
      const result = await onSubmit(formData);

      return result;
    } catch (error: any) {
      const errorMessages = extractMessageFromError(error, formData, t);

      if (
        errorMessages.length === 1 &&
        errorMessages[0].errorMessage === 'defaultError'
      ) {
        // Display snack wih default error message.
        setSnackErrorOpened(true);
      } else {
        errorMessages.forEach(({ field, errorMessage }) => {
          setError(field, { message: errorMessage });
        });
      }

      return null;
    }
  };

  return (
    <form
      onSubmit={handleSubmit(submit)}
      style={{
        display: 'flex',
        flexDirection: 'column',
        flexGrow: 1,
      }}
    >
      <Stack
        direction="row"
        spacing={1}
        sx={{
          mt: 1,
          alignItems: 'flex-start',
        }}
      >
        <RaTextField
          name="lot_reference"
          label={t('Internal listing reference')}
          errors={errors}
          register={register}
          disabled={readOnly}
        />
        <LoadingButton
          sx={{ mt: '26px !important', flexShrink: 0 }}
          type="submit"
          variant="contained"
          color="primary"
          loadingIndicator={
            <CircularProgress disableShrink color="inherit" size={16} />
          }
          disabled={readOnly || !isDirty}
          loading={isSubmitting}
          loadingPosition="start"
          startIcon={<SaveIcon />}
        >
          {t('Update')}
        </LoadingButton>
      </Stack>
      <Snackbar
        open={snackErrorOpened}
        autoHideDuration={6000}
        onClose={handleSnackClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleSnackClose}
          severity="error"
          sx={{ width: '100%' }}
        >
          {t('An error occured while submitting your form')}
        </Alert>
      </Snackbar>
    </form>
  );
};

export const ListingReferences: React.FC<ListingReferencesProps> = ({
  listingId,
  onReferenceChanged,
  readOnly = false,
}) => {
  const { t, intlLocale } = useLocale();

  const { data, refetch, loading } = useQuery(GET_LISTING_REFERENCES, {
    variables: { id: listingId },
  });

  const [refreshRef, { loading: isRefreshing }] =
    useMutation(REFRESH_REFERENCE);

  const dateTimeFormat = new Intl.DateTimeFormat(intlLocale, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });

  const listingInternalRef =
    data?.get_listing_references?.listing?.lot_reference ?? null;

  const portalReferences = useMemo(() => {
    if (data?.get_listing_references?.reference_aliases_encoded == null) {
      return [];
    }

    const references = [
      ...data.get_listing_references.reference_aliases_encoded,
    ];

    if (
      listingInternalRef != null &&
      references.every(ref => ref.alias !== listingInternalRef)
    ) {
      references.push({ alias: listingInternalRef, created_at: null });
    }

    return references;
  }, [
    data?.get_listing_references?.reference_aliases_encoded,
    listingInternalRef,
  ]);

  return (
    <>
      <ReferenceForm
        listingRef={listingInternalRef}
        readOnly={readOnly}
        onSubmit={async ({ lot_reference }) => {
          await onReferenceChanged(lot_reference);
          refetch();
        }}
      />
      <FormLabel
        sx={{
          mt: 2,
          marginBottom: '0.5rem',
          fontWeight: 500,
          display: 'block',
        }}
      >
        {t('Portal listing references')}
      </FormLabel>
      {loading ? (
        <ListingReferencesSkeleton />
      ) : (
        <Box>
          <Stack
            direction="row"
            spacing={1}
            sx={{
              flexWrap: 'wrap',
            }}
          >
            {portalReferences.map(({ alias, created_at }, index) => (
              <Tooltip
                title={
                  created_at != null
                    ? dateTimeFormat.format(new Date(created_at))
                    : undefined
                }
                placement="top"
                key={alias}
              >
                <Chip
                  label={alias}
                  sx={{
                    '&.MuiChip-root': { cursor: 'default' },
                    mb: '8px !important',
                  }}
                  color={index === 0 ? 'primary' : undefined}
                  icon={index === 0 ? <CheckIcon size={18} /> : undefined}
                />
              </Tooltip>
            ))}
          </Stack>

          <LoadingButton
            sx={{ mt: 1.5 }}
            type="button"
            variant="outlined"
            color="primary"
            loadingIndicator={
              <CircularProgress disableShrink color="inherit" size={16} />
            }
            loading={isRefreshing}
            disabled={readOnly}
            onClick={() =>
              refreshRef({
                variables: { id: listingId },
                onCompleted: () => refetch(),
              })
            }
            loadingPosition="start"
            startIcon={<RefreshCwIcon />}
          >
            {t('Refresh')}
          </LoadingButton>
        </Box>
      )}
    </>
  );
};
